import type { PreparedCall, TradeIntent, TradeQuote } from '@hoodmorning/contracts';
import {
  decodeAbiParameters, decodeFunctionData, encodeAbiParameters, encodeFunctionData,
  getAddress, isAddressEqual, parseAbi, parseAbiParameters, zeroAddress, type Address,
} from 'viem';
import { hashPreparedCalls } from './calls.js';
import { inspectRouterExecution, routerExecuteAbi } from './routerInspection.js';
import { inspectRouterExecutionV212 } from './routerInspectionV212.js';
import { terminalRouterVersionForDecoder } from './terminalRouterVersion.js';
import { validatePreparedTrade, type TradePolicy, type VerifiedTradeDecoding } from './tradePolicy.js';

const swapParameters = parseAbiParameters('address recipient,uint256 amountIn,uint256 amountOutMin,address[] path,bool payerIsUser,uint256[] minHopPriceX36');
const transitionParameters = parseAbiParameters('address recipient,uint256 amount');
const paymentParameters = parseAbiParameters('address token,address recipient,uint256 amount');
const approvalAbi = parseAbi(['function approve(address spender,uint256 amount) returns (bool)']);
const permit2ApprovalAbi = parseAbi(['function approve(address token,address spender,uint160 amount,uint48 expiration)']);
const routerRecipient: Address = '0x0000000000000000000000000000000000000002';
const senderRecipient: Address = '0x0000000000000000000000000000000000000001';
const fullPrecision = 10n ** 18n;
const contractBalance = 1n << 255n;
const maximumPermit2Amount = (1n << 160n) - 1n;

export type ActualNativeV2TradeDecoding = VerifiedTradeDecoding & {
  /** Actual ERC-20 pool endpoints, not the intent's native asset label. */
  route: { family: 'V2'; tokens: Address[] };
  nativeDirection: 'INPUT' | 'OUTPUT';
  nativeWrapper: Address;
  /** SDK quote-step output fees are ETH after unwrap, not legacy WETH fees. */
  feeAsset: Address | 'NATIVE';
};

/** One-pool native V2 exact-input calldata policy prerequisite.
 * Reference checked 30 September 2026: Universal Router 2.1.1 commit
 * 999d561c3ad58fb5cab91b602911f3c75591a9c7 (Dispatcher, Payments,
 * V2SwapRouter), preserved external-router-association source manifest.
 * Version 2.1.2 references retained 5 October 2026: commit
 * 802fe4c18f47300e0f183e2a42e9146ec2ea9fc3 from
 * external-router-v212-association; installed SDK 5.11.3/5.12.0
 * SwapRouter.encodeSwaps quote-step envelopes. Only the captured server-policy
 * decoder pin selects the exact 5.11.3/2.1.1 or 5.12.0/2.1.2 reader;
 * compatible bytes and quote metadata cannot select or upgrade it.
 * Native input: exact wrap -> router-paid swap -> ERC-20 fee -> net sweep.
 * Native output: vault-paid swap -> gross unwrap to router -> ETH fee -> sweep.
 * The caller must independently verify nativeWrapper against router WETH9,
 * runtime/backing, source/pool identities, pre-balances, allowances, simulation,
 * gas/refunds and canonical cashflows. This structural result proves none of
 * those facts and grants no signing, sending, settlement or release authority.
 * No legacy WETH-fee flow, extra ingress/refund/safeMode sweep, or other route
 * family is admitted. Existing ERC-20 validators are intentionally unchanged.
 */
export function decodeAndValidateNativeV2Trade(input: {
  intent: TradeIntent<4663 | 46630>; quote: TradeQuote; calls: readonly PreparedCall[];
  policy: TradePolicy; nativeWrapper: Address;
}): ActualNativeV2TradeDecoding {
  const { intent, quote, policy } = input;
  const routerVersion = terminalRouterVersionForDecoder(policy.requiredDecoderVersion);
  const nativeInput = intent.tokenIn === 'NATIVE';
  const nativeOutput = intent.tokenOut === 'NATIVE';
  if (nativeInput === nativeOutput) throw new Error('Exactly one native endpoint required');
  if (intent.type !== 'EXACT_INPUT') throw new Error('Native V2 exact input required');
  const nativeWrapper = getAddress(input.nativeWrapper);
  if ([zeroAddress, senderRecipient, routerRecipient].some(address => isAddressEqual(nativeWrapper, address))) {
    throw new Error('Invalid reviewed native wrapper');
  }
  if ([zeroAddress, senderRecipient, routerRecipient].some(address => isAddressEqual(policy.feeRecipient, address))) {
    throw new Error('Fee recipient must be an explicit treasury');
  }
  const tokenIn = nativeInput ? nativeWrapper : getAddress(intent.tokenIn as Address);
  const tokenOut = nativeOutput ? nativeWrapper : getAddress(intent.tokenOut as Address);
  const calls = input.calls.map(call => ({ ...call }));
  if (nativeInput ? calls.length !== 1 : ![1, 2, 4].includes(calls.length)) {
    throw new Error('Unsupported native call coverage');
  }
  const callIndex = calls.length - 1;
  const call = calls[callIndex]!;
  if (!isAddressEqual(call.to, policy.router)
    || call.value !== (nativeInput ? BigInt(intent.amountIn) : 0n)) throw new Error('Invalid native router call/value');
  const inspected = routerVersion === '2.1.1'
    ? inspectRouterExecution(call.data, policy.nowUnixSeconds)
    : inspectRouterExecutionV212(call.data, policy.nowUnixSeconds);
  const { args: [commands, inputs] } = decodeFunctionData({ abi: routerExecuteAbi, data: call.data });
  if (commands.toLowerCase() !== (nativeInput ? '0x0b080704' : '0x080c0704')) {
    throw new Error('Unsupported native command flow');
  }
  const swapIndex = nativeInput ? 1 : 0;
  const transitionIndex = nativeInput ? 0 : 1;
  const swap = decodeAbiParameters(swapParameters, inputs[swapIndex]!);
  const transition = decodeAbiParameters(transitionParameters, inputs[transitionIndex]!);
  const fee = decodeAbiParameters(paymentParameters, inputs[2]!);
  const sweep = decodeAbiParameters(paymentParameters, inputs[3]!);
  if (encodeAbiParameters(swapParameters, swap).toLowerCase() !== inputs[swapIndex]!.toLowerCase()
    || encodeAbiParameters(transitionParameters, transition).toLowerCase() !== inputs[transitionIndex]!.toLowerCase()
    || encodeAbiParameters(paymentParameters, fee).toLowerCase() !== inputs[2]!.toLowerCase()
    || encodeAbiParameters(paymentParameters, sweep).toLowerCase() !== inputs[3]!.toLowerCase()) {
    throw new Error('Noncanonical native command input');
  }
  const [recipient, amountIn, grossMinimum, path, payerIsUser, hopPrices] = swap;
  if (amountIn === 0n || amountIn === contractBalance
    || (!nativeInput && amountIn > maximumPermit2Amount)) throw new Error('Unsupported native swap input amount');
  if (!isAddressEqual(recipient, routerRecipient) || payerIsUser !== !nativeInput) {
    throw new Error('Invalid native swap custody/payer');
  }
  if (path.length !== 2 || (hopPrices.length !== 0 && hopPrices.length !== 1)
    || path.some(token => isAddressEqual(token, zeroAddress))
    || isAddressEqual(path[0]!, path[1]!)) throw new Error('One distinct V2 pool required');
  if (!isAddressEqual(path[0]!, tokenIn) || !isAddressEqual(path[1]!, tokenOut)) {
    throw new Error('Native wrapped route asset mismatch');
  }
  if (!isAddressEqual(transition[0], routerRecipient)
    || transition[1] !== (nativeInput ? amountIn : grossMinimum)) {
    throw new Error('Invalid exact native wrap/unwrap');
  }
  const paymentAsset = nativeOutput ? zeroAddress : tokenOut;
  if (!isAddressEqual(fee[0], paymentAsset) || !isAddressEqual(sweep[0], paymentAsset)) {
    throw new Error('Native fee/sweep asset mismatch');
  }
  // Dispatcher loads SWEEP.amountMin into uint160, despite its uint256 ABI
  // word. Do not report a full-width reviewed minimum that execution truncates.
  if (sweep[2] > maximumPermit2Amount) throw new Error('Sweep minimum exceeds deployed uint160 width');
  if (fee[2] !== BigInt(policy.requiredFeeBps) * 10n ** 14n) throw new Error('Native fee rate mismatch');
  const recipientIsSender = isAddressEqual(sweep[1], senderRecipient);
  if (!recipientIsSender && !isAddressEqual(sweep[1], policy.vault)) throw new Error('Native sweep recipient is not vault');
  // The swap itself must guarantee the entire net sweep floor; an unrelated
  // pre-existing router balance must not be needed to meet either minimum.
  const netSwapMinimum = grossMinimum - grossMinimum * fee[2] / fullPrecision;
  if (netSwapMinimum < sweep[2] || sweep[2] < BigInt(quote.minimumAmountOut)) {
    throw new Error('Native net swap/sweep minimum too low');
  }
  const decoded: ActualNativeV2TradeDecoding = {
    verified: true, decoder: inspected.decoder, decoderVersion: inspected.decoderVersion,
    routerVersion: inspected.routerVersion, callsHash: hashPreparedCalls(calls), commands: [],
    route: { family: 'V2', tokens: path.map(token => getAddress(token)) },
    nativeDirection: nativeInput ? 'INPUT' : 'OUTPUT', nativeWrapper,
    feeAsset: nativeOutput ? 'NATIVE' : tokenOut,
  };
  // Retain the existing exact single approval or zero-reset/token/Permit2
  // setup coverage. Current allowance readiness still belongs to the caller.
  for (let index = 0; index < (callIndex === 3 ? 2 : callIndex); index++) {
    const approval = calls[index]!;
    if (!isAddressEqual(approval.to, tokenIn) || approval.value !== 0n) throw new Error('Invalid native-output approval call');
    const parsed = decodeFunctionData({ abi: approvalAbi, data: approval.data });
    if (encodeFunctionData({ abi: approvalAbi, functionName: 'approve', args: parsed.args }).toLowerCase() !== approval.data.toLowerCase()) {
      throw new Error('Noncanonical native-output approval');
    }
    decoded.commands.push({ kind: 'APPROVAL', callIndex: index, token: approval.to,
      spender: parsed.args[0], amount: parsed.args[1] });
  }
  if (callIndex === 3) {
    const approval = calls[2]!;
    if (approval.value !== 0n || !policy.permittedApprovalSpenders.some(spender => isAddressEqual(spender, approval.to))
      || isAddressEqual(approval.to, tokenIn) || isAddressEqual(approval.to, policy.router)) {
      throw new Error('Invalid native-output Permit2 approval target');
    }
    const parsed = decodeFunctionData({ abi: permit2ApprovalAbi, data: approval.data });
    if (encodeFunctionData({ abi: permit2ApprovalAbi, functionName: 'approve', args: parsed.args }).toLowerCase() !== approval.data.toLowerCase()) {
      throw new Error('Noncanonical native-output Permit2 approval');
    }
    decoded.commands.push({ kind: 'PERMIT2_APPROVAL', callIndex: 2, permit2: approval.to,
      token: parsed.args[0], spender: parsed.args[1], amount: parsed.args[2], expiration: BigInt(parsed.args[3]) });
  }
  decoded.commands.push({ kind: 'SWAP', callIndex, router: call.to, swapper: policy.vault,
    recipient: recipientIsSender ? policy.vault : sweep[1],
    tokenIn: nativeInput ? 'NATIVE' : tokenIn, tokenOut: nativeOutput ? 'NATIVE' : tokenOut,
    amountIn, minimumAmountOut: sweep[2], deadlineUnixSeconds: inspected.deadline,
    feeBps: Number(fee[2] / 10n ** 14n), feeRecipient: fee[1] });
  const validation = validatePreparedTrade({ intent, quote, policy, calls, decoded });
  if (!validation.valid) throw new Error(`Native trade policy rejected: ${validation.failures.join(', ')}`);
  return decoded;
}
